import { c as _c } from "react/compiler-runtime";
import { Heading } from "@/components/ui/Heading";
import { Text } from "@/components/ui/Text";
import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import cn from "twsx";
type Gap = "xs" | "sm" | "md" | "lg" | "xl";
const gapClass: Record<Gap, string> = {
  xs: "gap-stack-xs",
  sm: "gap-stack-sm",
  md: "gap-stack-md",
  lg: "gap-stack-lg",
  xl: "gap-stack-xl"
};
type LayoutProps = React.ComponentProps<"div"> & {
  asChild?: boolean;
};
export function Stack(t0) {
  const $ = _c(12);
  let asChild;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      gap: t1,
      asChild,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asChild;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    asChild = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const gap = t1 === undefined ? "md" : t1;
  const Comp = asChild ? Slot : "div";
  const t2 = gapClass[gap];
  let t3;
  if ($[5] !== className || $[6] !== t2) {
    t3 = cn("flex min-w-0 flex-col", t2, className);
    $[5] = className;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== Comp || $[9] !== props || $[10] !== t3) {
    t4 = <Comp className={t3} {...props} />;
    $[8] = Comp;
    $[9] = props;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
export function Inline(t0) {
  const $ = _c(17);
  let asChild;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      gap: t1,
      wrap: t2,
      justify: t3,
      asChild,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asChild;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    asChild = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  const gap = t1 === undefined ? "md" : t1;
  const wrap = t2 === undefined ? false : t2;
  const justify = t3 === undefined ? "start" : t3;
  const Comp = asChild ? Slot : "div";
  const t4 = gapClass[gap];
  const t5 = wrap && "flex-wrap";
  const t6 = justify === "between" && "justify-between";
  const t7 = justify === "end" && "justify-end";
  let t8;
  if ($[7] !== className || $[8] !== t4 || $[9] !== t5 || $[10] !== t6 || $[11] !== t7) {
    t8 = cn("flex min-w-0 items-center", t4, t5, t6, t7, className);
    $[7] = className;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== Comp || $[14] !== props || $[15] !== t8) {
    t9 = <Comp className={t8} {...props} />;
    $[13] = Comp;
    $[14] = props;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}
const gridColumns = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
  auto: "grid-cols-cards",
  aside: "lg:grid-cols-main-aside",
  rail: "lg:grid-cols-main-rail",
  asideStart: "xl:grid-cols-aside-main"
} as const;
export function Grid(t0) {
  const $ = _c(14);
  let asChild;
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      columns: t1,
      gap: t2,
      asChild,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asChild;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    asChild = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const columns = t1 === undefined ? 3 : t1;
  const gap = t2 === undefined ? "lg" : t2;
  const Comp = asChild ? Slot : "div";
  const t3 = gridColumns[columns];
  const t4 = gapClass[gap];
  let t5;
  if ($[6] !== className || $[7] !== t3 || $[8] !== t4) {
    t5 = cn("grid min-w-0", t3, t4, className);
    $[6] = className;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== Comp || $[11] !== props || $[12] !== t5) {
    t6 = <Comp className={t5} {...props} />;
    $[10] = Comp;
    $[11] = props;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
const insetClass = {
  sm: "p-inset-sm",
  md: "p-inset-md",
  lg: "p-inset-lg"
} as const;
export function Inset(t0) {
  const $ = _c(12);
  let asChild;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      size: t1,
      asChild,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asChild;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    asChild = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const size = t1 === undefined ? "md" : t1;
  const Comp = asChild ? Slot : "div";
  const t2 = insetClass[size];
  let t3;
  if ($[5] !== className || $[6] !== t2) {
    t3 = cn(t2, className);
    $[5] = className;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== Comp || $[9] !== props || $[10] !== t3) {
    t4 = <Comp className={t3} {...props} />;
    $[8] = Comp;
    $[9] = props;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
const containerWidth = {
  phone: "max-w-phone",
  narrow: "max-w-narrow",
  default: "max-w-form",
  wide: "max-w-wide",
  full: "max-w-none"
} as const;
export type ContainerWidth = keyof typeof containerWidth;

/** Use when a screen needs layout primitives: Stack and Inline for spacing, Grid for columns, Section for titled groups, Container for a width cap. */
export function Container(t0) {
  const $ = _c(10);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      width: t1,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const width = t1 === undefined ? "default" : t1;
  const t2 = containerWidth[width];
  let t3;
  if ($[4] !== className || $[5] !== t2) {
    t3 = cn("mx-auto w-full min-w-0", t2, className);
    $[4] = className;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props || $[8] !== t3) {
    t4 = <div className={t3} {...props} />;
    $[7] = props;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export function Section(t0) {
  const $ = _c(23);
  let actions;
  let children;
  let className;
  let description;
  let props;
  let t1;
  let title;
  if ($[0] !== t0) {
    ({
      title,
      description,
      actions,
      variant: t1,
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = actions;
    $[2] = children;
    $[3] = className;
    $[4] = description;
    $[5] = props;
    $[6] = t1;
    $[7] = title;
  } else {
    actions = $[1];
    children = $[2];
    className = $[3];
    description = $[4];
    props = $[5];
    t1 = $[6];
    title = $[7];
  }
  const variant = t1 === undefined ? "plain" : t1;
  const hasHeader = title || description || actions;
  let t2;
  if ($[8] !== className) {
    t2 = cn("flex min-w-0 flex-col gap-stack-sm", className);
    $[8] = className;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== actions || $[11] !== description || $[12] !== hasHeader || $[13] !== title) {
    t3 = hasHeader && <div className="flex min-h-6 items-center justify-between gap-3"><div className="flex min-w-0 flex-col gap-0.5">{title && <Heading level={2} size="sm">{title}</Heading>}{description && <Text as="p" size="sm" tone="tertiary">{description}</Text>}</div>{actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}</div>;
    $[10] = actions;
    $[11] = description;
    $[12] = hasHeader;
    $[13] = title;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== children || $[16] !== variant) {
    t4 = variant === "card" ? <div className={cn("flex flex-col divide-y divide-border-subtle", "rounded-settings-list-view-item bg-bg-surface [box-shadow:var(--elevation-card)]")}>{children}</div> : children;
    $[15] = children;
    $[16] = variant;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== props || $[19] !== t2 || $[20] !== t3 || $[21] !== t4) {
    t5 = <section className={t2} {...props}>{t3}{t4}</section>;
    $[18] = props;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return t5;
}